import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useStore } from "../../../helpers/helpers";
import { htmlValidation, TextError } from "../../../helpers/utils";
import { safe_get } from "../../../report-visuals/report-utils";
import { CreateModel, FormButtons } from "../../../components";

export const AddEditCampaign = observer((props: any) => {
  const params = useParams();
  const projectId = params.projectid;

  const { voterReachOutStore } = useStore();
  const { reachOutList, reachOutTargetList } = voterReachOutStore;

  const [messageAutoCompleteOptions, setMessageAutoCompleteOptions] =
    useState<any[]>([]);
  const [targetAutoCompleteOptions, setTargetAutoCompleteOptions] =
    useState<any[]>([]);

  const [selectedMessage, setSelectedMessage] = useState<any[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<any[]>([]);
  const [selectedName, setSelectedName] = useState("");

  const [isSubmit, setSubmit] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    setMessageAutoCompleteOptions(reachOutList ?? []);
  }, [reachOutList]);

  useEffect(() => {
    setTargetAutoCompleteOptions(reachOutTargetList ?? []);
  }, [reachOutTargetList]);

  const createVroc = async () => {
    try {
      const msgId = selectedMessage.map((item) => item.id).join(",");
      const tgtId = selectedTarget.map((item) => item.id).join(",");

      const payload = {
        projectid: projectId,
        name: selectedName.trim(),
        msgid: Number(msgId),
        tgtid: Number(tgtId),
      };

      const res = await voterReachOutStore.create_reachout_Campaign(payload);

      if (res.status === 200) {
        toast.success("Campaign message added successfully");

        voterReachOutStore.lastReachOutCampaignPayload = null;
        props.getReachOutCampaign();
        voterReachOutStore.setAddCampaignStatus(false);
        setIsCreateModalOpen(false);
      } else if (res.status === 400) {
        toast.error("Insufficient balance to create campaign");
        voterReachOutStore.setAddCampaignStatus(false);
        setIsCreateModalOpen(false);
      } else {
        toast.error("Something went wrong. Please try again later.");
        voterReachOutStore.setAddCampaignStatus(false);
        setIsCreateModalOpen(false);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again later.");
      console.error("Campaign creation failed:", error);
    }
  };

  const handleSubmit = () => {
    setSubmit(true);

    if (
      selectedName.trim().length > 0 &&
      selectedMessage.length > 0 &&
      selectedTarget.length > 0
    ) {
      setIsCreateModalOpen(true);
    }
  };

  const autocompleteStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#ffffff",
      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      "& fieldset": {
        borderColor: "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: "#a5b4fc",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.10)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6366f1",
        borderWidth: "1px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#4f46e5",
    },
  };

  const renderOption = (props: any, option: any, type: "message" | "target") => {
    const name =
      type === "message"
        ? safe_get(option, "ds.name", "")
        : safe_get(option, "name", "");

    return (
      <Box
        component="li"
        {...props}
        key={option.id}
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          py: 1.25,
          px: 1.5,
          borderRadius: "8px",
          mx: 0.5,
          "&:hover": {
            backgroundColor: "#eef2ff !important",
          },
        }}
      >
        {option.profileimageuri ? (
          <img
            loading="lazy"
            width="40"
            height="40"
            src={option.profileimageuri}
            alt=""
            className="h-10 w-10 rounded-full border border-gray-200 object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-sm font-semibold text-indigo-600">
            {name?.charAt(0)?.toUpperCase() || "?"}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-800">
            {name || "Unnamed"}
          </p>
          <p className="mt-0.5 truncate text-xs text-gray-500">
            {safe_get(option, "phonenum", "No phone number")}
          </p>
        </div>
      </Box>
    );
  };

  return (
    <>
      <div className="mx-auto w-full max-w-4xl">
        {/* Form card */}
        <div className="overflow-visible rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4 sm:px-7">
            <h3 className="text-sm font-semibold text-gray-900">
              Campaign Details
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              Complete all three fields to continue.
            </p>
          </div>

          <div className="space-y-6 p-5 sm:p-7">
            {/* Campaign name */}
            <div>
              <label
                htmlFor="campaign-name"
                className="mb-2 block text-sm font-semibold text-gray-700"
              >
                Campaign Name <span className="text-red-500">*</span>
              </label>

              <TextField
                id="campaign-name"
                fullWidth
                value={selectedName}
                placeholder="Enter a campaign name"
                onChange={(event) => {
                  htmlValidation(event);
                  setSelectedName(event.target.value);
                }}
                error={isSubmit && selectedName.trim().length === 0}
                sx={autocompleteStyles}
                InputProps={{
                  startAdornment: (
                    <span className="mr-2 flex items-center text-gray-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        className="h-5 w-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 7h16M4 12h16M4 17h10"
                        />
                      </svg>
                    </span>
                  ),
                }}
              />

              {isSubmit && selectedName.trim().length === 0 && (
                <div className="mt-1.5">
                  <TextError error="Please enter the campaign name" />
                </div>
              )}
            </div>

            {/* Message selection */}
            <div>
              <label
                htmlFor="select-message"
                className="mb-2 block text-sm font-semibold text-gray-700"
              >
                Message <span className="text-red-500">*</span>
              </label>

              <p className="mb-2 text-xs text-gray-500">
                Select the message or messages to use in this campaign.
              </p>

              <Autocomplete
                multiple
                id="select-message"
                options={messageAutoCompleteOptions}
                value={selectedMessage}
                onChange={(event, value) => {
                  htmlValidation(event);
                  setSelectedMessage([...value]);
                }}
                autoHighlight
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => safe_get(option, "ds.name", "")}
                renderOption={(optionProps, option) =>
                  renderOption(optionProps, option, "message")
                }
                sx={autocompleteStyles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedMessage.length
                        ? ""
                        : "Search and select messages"
                    }
                    error={isSubmit && selectedMessage.length === 0}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                  />
                )}
              />

              {isSubmit && selectedMessage.length === 0 && (
                <div className="mt-1.5">
                  <TextError error="Please select at least one message" />
                </div>
              )}
            </div>

            {/* Target selection */}
            <div>
              <label
                htmlFor="select-target"
                className="mb-2 block text-sm font-semibold text-gray-700"
              >
                Target Audience <span className="text-red-500">*</span>
              </label>

              <p className="mb-2 text-xs text-gray-500">
                Choose the target list for your campaign.
              </p>

              <Autocomplete
                multiple
                id="select-target"
                options={targetAutoCompleteOptions}
                value={selectedTarget}
                onChange={(event, value) => {
                  htmlValidation(event);
                  setSelectedTarget([...value]);
                }}
                autoHighlight
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => safe_get(option, "name", "")}
                renderOption={(optionProps, option) =>
                  renderOption(optionProps, option, "target")
                }
                sx={autocompleteStyles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedTarget.length
                        ? ""
                        : "Search and select a target list"
                    }
                    error={isSubmit && selectedTarget.length === 0}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "new-password",
                    }}
                  />
                )}
              />

              {isSubmit && selectedTarget.length === 0 && (
                <div className="mt-1.5">
                  <TextError error="Please select at least one target list" />
                </div>
              )}
            </div>
          </div>

          {/* Form actions */}
          <div className="border-t border-gray-100 bg-gray-50/70 px-5 py-4 sm:px-7">
            <FormButtons
              disabled={
                !selectedName.trim() ||
                selectedMessage.length === 0 ||
                selectedTarget.length === 0
              }
              onSave={handleSubmit}
              onCancel={() => {
                voterReachOutStore.setAddCampaignStatus(false);
              }}
            />
          </div>
        </div>

      </div>

      {isCreateModalOpen && (
        <CreateModel
          isOpen={isCreateModalOpen}
          desc="Are you sure you want to create a new campaign?"
          onCreateClick={createVroc}
          onCancelClick={() => setIsCreateModalOpen(false)}
        />
      )}
    </>
  );
});